import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { $sortingOrder } from '../../../shared/constants/sort';
import {
  IAutoRemediationReportByRule,
  IScannableDocumentReportByRule,
} from '../../../shared/interfaces/scannable-document-report.interface';
import { IScannableDocumentRule } from '../../../shared/interfaces/scannable-document-rule.interface';
import {
  IAutoRemediationStateResponse,
  IEditScannableDocumentRequest,
  IScannableDocument,
  IScannableDocumentPdfPagesTestStateResponse,
  IScannableDocumentsResponse,
  IScannableDocumentUploadRequest,
  IScannableDocumentUploadResponse,
  IStartAutoRemediationsResponse,
} from '../../../shared/interfaces/scannable-document.interface';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { FeatureFlagCollection } from '../../../shared/interfaces/feature-flag.interface';
import { AngularUtility } from '../utility/angular.utility';
import { RestService } from './rest.service';
import { ScannableDocumentApi } from './rest/scannable-document.api';
import { UserPropertyService } from './user-property.service';
import { ICurrentSelectedProperty } from './user.service';
import { AiService } from './ai.service';
import { FeatureFlagService } from './feature-flag/feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class ScannableDocumentService {
  public isPdfAutoRemediationEnabled$: Observable<boolean>;

  constructor(
    private restService: RestService,
    private scannableDocumentApi: ScannableDocumentApi,
    private userPropertyService: UserPropertyService,
    private featureFlagService: FeatureFlagService,
    private aiService: AiService,
  ) {
    this.isPdfAutoRemediationEnabled$ = combineLatest([
      this.featureFlagService.variation$(FeatureFlagCollection.laPdfAutoRemediation, false),
      this.aiService.aiEnabledForTenant$,
    ]).pipe(
      map(
        ([isPdfAutoRemediationFeatureFlagEnabled, isAiEnabled]: [boolean, boolean]): boolean =>
          Boolean(isPdfAutoRemediationFeatureFlagEnabled) && isAiEnabled,
      ),
      AngularUtility.shareRef(),
    );
  }

  public createScannableDocument(
    formData: FormData,
    onProgress: (progress: number) => void,
  ): Observable<IScannableDocumentUploadResponse> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocumentUploadResponse> => {
        return this.restService.createScannableDocument(workspaceId, formData, onProgress);
      }),
    );
  }

  public removeScannableDocument(scannableDocumentId: string | string[]): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.removeScannableDocument(workspaceId, scannableDocumentId);
      }),
    );
  }

  public createScannableDocumentFromUrls(urls: string[], scanId?: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId, digitalPropertyId }: ICurrentSelectedProperty): Observable<void> => {
        const request: IScannableDocumentUploadRequest = {
          urls,
        };

        const isRequestFromScanUrl = (): boolean => SharedCommonUtility.notNullish(scanId);

        const addScanInfoToRequest = (): void => {
          request.scanId = scanId;
          request.digitalPropertyId = digitalPropertyId;
        };

        if (isRequestFromScanUrl()) {
          addScanInfoToRequest();
        }

        return this.scannableDocumentApi.createScannableDocumentFromLink(workspaceId, request);
      }),
    );
  }

  public testScannableDocument(scannableDocumentId: string | string[]): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.startDocumentScan(workspaceId, scannableDocumentId);
      }),
    );
  }

  public requestRemediationScannableDocument(scannableDocumentIds: string[]): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.requestRemediationDocumentScan(workspaceId, scannableDocumentIds);
      }),
    );
  }

  public getReportForDocuments(): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.getReportForDocuments(workspaceId);
      }),
    );
  }

  public getScannableDocuments(
    limit: number,
    page: number,
    term: string,
    sortBy: string = null,
    sortOrder: $sortingOrder = null,
    documentIds: string[] = null,
  ): Observable<IScannableDocumentsResponse> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocumentsResponse> => {
        return this.scannableDocumentApi.getScannableDocuments(workspaceId, limit, page, term, sortBy, sortOrder, documentIds);
      }),
    );
  }

  public getScannableDocument(scannableDocumentId: string): Observable<IScannableDocument> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocument> => {
        return this.scannableDocumentApi.getScannableDocument(workspaceId, scannableDocumentId);
      }),
    );
  }

  public editScannableDocument(scannableDocumentId: string, editRequest: IEditScannableDocumentRequest): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.editScannableDocument(workspaceId, scannableDocumentId, editRequest);
      }),
    );
  }

  public getScannableDocumentReportByRule(
    scannableDocumentId: string,
    scannableDocumentRuleId: string,
  ): Observable<IScannableDocumentReportByRule> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocumentReportByRule> => {
        return this.scannableDocumentApi.getScannableDocumentReportByRule(
          workspaceId,
          scannableDocumentId,
          scannableDocumentRuleId,
        );
      }),
    );
  }

  public getAutoRemediationReportByRule(
    scannableDocumentId: string,
    scannableDocumentRuleId: string,
  ): Observable<IAutoRemediationReportByRule> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IAutoRemediationReportByRule> => {
        return this.scannableDocumentApi.getAutoRemediationReportByRule(
          workspaceId,
          scannableDocumentId,
          scannableDocumentRuleId,
        );
      }),
    );
  }

  public getRuleById(scannableDocumentRuleId: string): Observable<IScannableDocumentRule> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocumentRule> => {
        return this.scannableDocumentApi.getRuleById(workspaceId, scannableDocumentRuleId);
      }),
    );
  }

  public getAllRules(): Observable<IScannableDocumentRule[]> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocumentRule[]> => {
        return this.scannableDocumentApi.getAllRules(workspaceId);
      }),
    );
  }

  public downloadTestReport(scannableDocumentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.downloadTestReport(workspaceId, scannableDocumentId);
      }),
    );
  }

  public getPdfPagesTestState(): Observable<IScannableDocumentPdfPagesTestStateResponse> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocumentPdfPagesTestStateResponse> => {
        return this.scannableDocumentApi.getPdfPagesTestState(workspaceId);
      }),
    );
  }

  public removeRemediatedDocument(scannableDocumentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.removeRemediatedDocument(workspaceId, scannableDocumentId);
      }),
    );
  }

  public revokeRemediationRequest(scannableDocumentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.revokeRemediationRequest(workspaceId, scannableDocumentId);
      }),
    );
  }

  public downloadDocument(scannableDocumentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.downloadDocument(workspaceId, scannableDocumentId);
      }),
    );
  }

  public startAutoRemediation(scannableDocumentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.startAutoRemediation(workspaceId, scannableDocumentId);
      }),
    );
  }

  public startAutoRemediations(scannableDocumentIds: string[]): Observable<IStartAutoRemediationsResponse> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IStartAutoRemediationsResponse> => {
        return this.scannableDocumentApi.startAutoRemediations(workspaceId, scannableDocumentIds);
      }),
    );
  }

  public getAutoRemediationState(scannableDocumentId: string): Observable<IAutoRemediationStateResponse> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IAutoRemediationStateResponse> => {
        return this.scannableDocumentApi.getAutoRemediationState(workspaceId, scannableDocumentId);
      }),
    );
  }

  public uploadAutoRemediatedPdf(scannableDocumentId: string): Observable<IScannableDocument> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<IScannableDocument> => {
        return this.scannableDocumentApi.uploadAutoRemediatedPdf(workspaceId, scannableDocumentId);
      }),
    );
  }

  public setRemediatedDocument(scannableDocumentId: string, remediatedDocumentId: string): Observable<void> {
    return this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ workspaceId }: ICurrentSelectedProperty): Observable<void> => {
        return this.scannableDocumentApi.setRemediatedDocument(workspaceId, scannableDocumentId, remediatedDocumentId);
      }),
    );
  }
}
